import {
  AlertIntegrationType,
  EtlTool,
} from '@elementary/common-ui/services/gql/types';
import { SourceType, TicketingTool } from 'shared/types';
import { PipelineIncidentData } from 'shared/components/Pipeline/types';

export type Status = 'Open' | 'Acknowledged' | 'Resolved';
export type Severity = 'Low' | 'Normal' | 'High' | 'Critical';

export type GraphQLStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED';
export type GraphQLSeverity = 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';

export type Ticket = {
  service: TicketingTool;
  service_ticket_url: string;
  service_ticket_id: string;
};

export type IncidentDetails = {
  severity: Severity;
  status: Status;
  assignee?: string;
};

export type Incident = {
  id: string;
  source_id: string;
  fully_qualified_name?: string;
  source_type: SourceType;
  severity: Severity;
  start_time: string;
  end_time: string;
  status: Status;
  last_fail: string;
  assignee?: string;
  test_name: string;
  total_fails: number;
  tickets: Ticket[];
  database_name: string;
  schema_name: string;
  table_name: string;
  column_name?: string;
  additional_data: {
    display_name: string;
    sub_type?: string;
    type?: string;
    column?: boolean;
  };
  sub_test_unique_id?: string;
};

export type FetchIncidentsApi = {
  options: { property: string; values: string[] }[];
};

export type FetchIncidentOverviewApi = {
  average_seconds_to_resolve: number;
  severities_per_status: Record<Status, Record<Severity, number>>;
};

type IncidentSource = {
  __typename?:
    | 'TestIncidentSource'
    | 'ModelIncidentSource'
    | 'PipelineIncidentSource';
  asset?: {
    id: string;
    dbName?: string;
    schemaName?: string;
    name: string;
    description?: string;
  };
  test?: {
    id: string;
    name: string;
    type: string;
    description?: string;
  };
  testExecution?: {
    id: string;
    subType: string;
    subTestUniqueId: string;
    columnName?: string;
  };
  modelExecution?: {
    id: string;
    status: string;
    query: string;
    executionTimestamp: string;
  };
  // Pipeline source fields
  connectorName?: string;
  connectorType?: string;
  toolType?: EtlTool;
  connectorId?: string;
  destination?: string | null;
  rootEventId?: string;
};

type TicketDetailsData = {
  service: 'JIRA' | 'LINEAR' | 'SERVICE_NOW';
  serviceTicketUrl: string;
  serviceTicketId: string;
};

export enum EventType {
  TEST_RUN = 'TEST_RUN',
  ETL_TOOL_RUN = 'ETL_TOOL_RUN',
  TEST_DELETED = 'TEST_DELETED',
  MODEL_RUN = 'MODEL_RUN',
  MODEL_DELETED = 'MODEL_DELETED',
  ASSIGNEE_CHANGE = 'ASSIGNEE_CHANGE',
  STATUS_CHANGE = 'STATUS_CHANGE',
  SEVERITY_CHANGE = 'SEVERITY_CHANGE',
  ALERT_SENT = 'ALERT_SENT',
  SUMMARY_GENERATED = 'SUMMARY_GENERATED',
  SUMMARY_EDITED = 'SUMMARY_EDITED',
  SUMMARY_DELETED = 'SUMMARY_DELETED',
  SUMMARY_SENT = 'SUMMARY_SENT',
  SUMMARY_WRITTEN = 'SUMMARY_WRITTEN',
}

export enum EventStatus {
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
  WARN = 'WARN',
  NO_STATUS = 'NO_STATUS',
}

export type AlertDestination = {
  type: AlertIntegrationType;
  channel?: string;
};

export type IncidentEventData = {
  changer_name?: string;
  changer_email?: string;
  changer_id?: string;
  source?: string;
  assignee?: string | null;
  status?: string;
  severity?: string;
  summary_type?: string;
  destination?: AlertDestination;
  slack_permalink?: string;
  // ETL_TOOL_RUN specific fields
  tool_type?: EtlTool;
  event?: string;
};

export type IncidentEvent = {
  id: string;
  eventTime: string;
  eventType: EventType;
  eventStatus: EventStatus;
  status?: string | null;
  exception?: string | null;
  description?: string | null;
  failures?: number | null;
  data?: IncidentEventData | null;
};

export type SummaryType = 'ROOT_CAUSE' | 'DOWNSTREAM_IMPACT' | 'USER_SUMMARY';

export type IncidentSummary = {
  id: string;
  summaryType: SummaryType;
  content: string;
};

export type IncidentDetailsResponse = {
  id: string;
  startTime: string;
  endTime?: string | null;
  displayName: string;
  message: string;
  assignee?: string | null;
  status: GraphQLStatus;
  severity: GraphQLSeverity;
  severityRank: number;
  tags: string[];
  owners: string[];
  subscribers: string[];
  runFailureEventCount: number;
  lastEventTime: string;
  lastFailureEventTime: string;
  tickets: TicketDetailsData[];
  summaries: IncidentSummary[];
  events: IncidentEvent[];
  source?: IncidentSource | null;
};

export type IncidentOverviewData = Omit<
  IncidentDetailsResponse,
  'status' | 'severity'
> & {
  status: Status;
  severity: Severity;
  sourceId: string;
  sourceType: SourceType;
  tableAssetId?: string;
  fullyQualifiedName?: string;
  testName?: string;
  testType?: string;
  testSubType?: string;
  testDescription?: string;
  modelDescription?: string;
  isColumn: boolean;
  pipelineData?: PipelineIncidentData;
};
